import { askAnswerEffect } from "./askAnswerEffect";
import { orderKarlAsks } from "./orderKarlAsks";
import type { KarlAnswer, KarlAsk, KarlAskTool } from "../karlAsks";
import type {
  KarlAskArm,
  KarlAskEffect,
  KarlAskQueueReducer,
  KarlAskQueueReducerResult,
  KarlAskQueueState,
  KarlAskRecord,
} from "./types";

interface DeferAskIdArgs {
  state: KarlAskQueueState;
  askId: string;
}

type DeferAskId = (args: DeferAskIdArgs) => string[];

interface SettleArgs {
  state: KarlAskQueueState;
  record: KarlAskRecord;
  before: KarlAskEffect[];
  after: KarlAskEffect[];
}

type Settle = (args: SettleArgs) => KarlAskQueueReducerResult;

interface ApplyAnswerArgs {
  state: KarlAskQueueState;
  ask: KarlAsk;
  answer: KarlAnswer;
}

type ApplyAnswer = (args: ApplyAnswerArgs) => KarlAskQueueReducerResult;

interface ApplyToolArgs {
  state: KarlAskQueueState;
  ask: KarlAsk;
  tool: KarlAskTool;
}

type ApplyTool = (args: ApplyToolArgs) => KarlAskQueueReducerResult;

interface JumpToAskArgs {
  state: KarlAskQueueState;
  asks: KarlAsk[];
  askId: string;
}

type JumpToAsk = (args: JumpToAskArgs) => KarlAskQueueReducerResult;

const isSettled = (history: KarlAskRecord[], askId: string): boolean =>
  history.some((record) => record.askId === askId);

const withoutAskId = (deferred: string[], askId: string): string[] =>
  deferred.filter((id) => id !== askId);

const deferAskId: DeferAskId = ({ state, askId }) => {
  if (state.deferred.includes(askId)) return state.deferred;
  if (isSettled(state.history, askId)) return state.deferred;
  return [...state.deferred, askId];
};

const settle: Settle = ({ state, record, before, after }) => {
  const slot = state.history.length - state.rewind;
  const revisiting = state.rewind > 0 && slot >= 0 && slot < state.history.length;
  const replaced = revisiting ? state.history[slot] : null;
  const revert = replaced?.revert ? [replaced.revert] : [];

  return {
    state: {
      history: revisiting
        ? state.history.map((entry, index) => (index === slot ? record : entry))
        : [...state.history, record],
      deferred: withoutAskId(state.deferred, record.askId),
      armed: null,
      rewind: 0,
    },
    effects: [...before, ...revert, ...after],
  };
};

const applyAnswer: ApplyAnswer = ({ state, ask, answer }) => {
  // Cancelling first: the arm he is abandoning must let go of the plan before
  // the new answer touches it.
  const before: KarlAskEffect[] = state.armed ? [{ kind: "cancelArm" }] : [];
  const disarmed: KarlAskQueueState = { ...state, armed: null };
  const plan = askAnswerEffect({ ask, answer });

  if (plan.isDeferral) {
    return {
      state: {
        ...disarmed,
        deferred: deferAskId({ state: disarmed, askId: ask.id }),
      },
      effects: [...before, ...plan.effects],
    };
  }

  if (plan.arm) {
    return {
      state: {
        ...disarmed,
        armed: { askId: ask.id, answerId: answer.id, arm: plan.arm },
      },
      effects: [...before, ...plan.effects],
    };
  }

  return settle({
    state: disarmed,
    record: {
      askId: ask.id,
      answerId: answer.id,
      outcome: plan.outcome,
      confirmKey: plan.confirmKey,
      revert: plan.revert,
    },
    before,
    after: plan.effects,
  });
};

// A tool's action vocabulary is an answer's, so the plan comes from the same
// place; what differs is that none of it is ever recorded.
const applyTool: ApplyTool = ({ state, ask, tool }) => {
  const before: KarlAskEffect[] = state.armed ? [{ kind: "cancelArm" }] : [];
  const plan = askAnswerEffect({
    ask,
    answer: { id: tool.id, label: "", action: tool.action },
  });

  return {
    state: {
      ...state,
      armed: plan.arm
        ? { askId: ask.id, answerId: tool.id, arm: plan.arm, isTool: true }
        : null,
    },
    effects: [...before, ...plan.effects],
  };
};

/**
 * Both halves of a jump, expressed in the state that already exists. A settled
 * ask is a rewind to its own record — the move `rewindAll` makes, stopping at
 * one ask instead of the first. A pending one is reached by deferring what
 * stands in front of it, and "Later" has always meant the back of the queue
 * rather than the bin, so nothing is lost and he can walk back to every one.
 */
const jumpToAsk: JumpToAsk = ({ state, asks, askId }) => {
  const letGo: KarlAskEffect[] = state.armed ? [{ kind: "cancelArm" }] : [];
  const settledAt = state.history.findIndex((record) => record.askId === askId);

  if (settledAt >= 0) {
    const rewind = state.history.length - settledAt;
    if (rewind === state.rewind && !state.armed) return { state, effects: [] };
    return { state: { ...state, armed: null, rewind }, effects: letGo };
  }

  const { queue } = orderKarlAsks({ asks, state });
  const at = queue.findIndex((ask) => ask.id === askId);
  // The rebuild no longer carries it. Standing still beats wedging the walk on
  // a question that does not exist.
  if (at < 0) return { state, effects: [] };
  if (at === 0 && state.rewind === 0 && !state.armed) {
    return { state, effects: [] };
  }

  const skipped = queue.slice(0, at).map((ask) => ask.id);
  const jumped = new Set(skipped);
  // A target already in `deferred` cannot simply be left there: the ones put
  // off before it would still come first. It goes to the front of the defer
  // order, and everything stepped over goes behind it.
  const deferred = state.deferred.includes(askId)
    ? [
        askId,
        ...state.deferred.filter((id) => id !== askId && !jumped.has(id)),
        ...skipped,
      ]
    : [...state.deferred, ...skipped];

  return {
    state: { ...state, deferred, armed: null, rewind: 0 },
    effects: letGo,
  };
};

/**
 * What committing an arm DOES, beyond settling the question. `moveSource`,
 * `placeSource` and `drawBox` do their work on the plan itself, so they name
 * nothing here. Read on both paths: an answer that arms real work has to
 * perform it too, not only a tool.
 */
const armWork = (arm: KarlAskArm): KarlAskEffect[] => {
  if (arm.kind === "boxAssign")
    return [{ kind: "assignSelection", circuitId: arm.circuitId }];
  if (arm.kind === "boxRemove") return [{ kind: "removeSelection" }];
  if (arm.kind === "pickRoute")
    return [
      { kind: "assignRemovalGroup", groupId: arm.groupId },
      { kind: "closeRemovalGroup", groupId: arm.groupId },
    ];
  return [];
};

export const karlAskQueueReducer: KarlAskQueueReducer = ({
  state,
  event,
  asks,
}) => {
  if (event.type === "answer") {
    return applyAnswer({ state, ask: event.ask, answer: event.answer });
  }

  if (event.type === "tool") {
    return applyTool({ state, ask: event.ask, tool: event.tool });
  }

  if (event.type === "commit") {
    if (!state.armed) return { state, effects: [] };
    // A tool performs its work and hands the question straight back: no record,
    // no defer, no rewind. Recording it would answer the ask for him.
    if (state.armed.isTool) {
      return {
        state: { ...state, armed: null },
        effects: armWork(state.armed.arm),
      };
    }
    return settle({
      state,
      record: {
        askId: state.armed.askId,
        answerId: state.armed.answerId,
        outcome: "committed",
      },
      before: [],
      after: armWork(state.armed.arm),
    });
  }

  if (event.type === "cancel") {
    if (!state.armed) return { state, effects: [] };
    return {
      state: { ...state, armed: null },
      effects: [{ kind: "cancelArm" }],
    };
  }

  if (event.type === "back") {
    if (state.rewind + 1 > state.history.length) return { state, effects: [] };
    return {
      state: { ...state, armed: null, rewind: state.rewind + 1 },
      effects: state.armed ? [{ kind: "cancelArm" }] : [],
    };
  }

  if (event.type === "forward") {
    if (state.rewind === 0) return { state, effects: [] };
    return { state: { ...state, rewind: state.rewind - 1 }, effects: [] };
  }

  // Back to the first thing he answered. Nothing is discarded: he walks the
  // same questions again and each new answer replaces its record in place, so
  // "go through them again" costs him nothing if he changes his mind.
  if (event.type === "rewindAll") {
    if (!state.history.length) return { state, effects: [] };
    return {
      state: { ...state, armed: null, rewind: state.history.length },
      effects: state.armed ? [{ kind: "cancelArm" }] : [],
    };
  }

  if (event.type === "jumpTo") {
    return jumpToAsk({ state, asks, askId: event.askId });
  }

  const vanished = new Set(event.askIds);
  const deferred = state.deferred.filter((id) => !vanished.has(id));
  const armedVanished = state.armed !== null && vanished.has(state.armed.askId);
  if (!armedVanished && deferred.length === state.deferred.length) {
    return { state, effects: [] };
  }

  return {
    state: {
      ...state,
      deferred,
      armed: armedVanished ? null : state.armed,
    },
    effects: armedVanished ? [{ kind: "cancelArm" }] : [],
  };
};
